import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { PropertyModal } from '../components/PropertyModal';
import { Search, MapPin, Users, Wifi, Utensils, Zap, Filter, ArrowRight, Calendar } from 'lucide-react';
import { Button } from '../components/Button';
import { cn } from '../utils/cn';
export function AvailableHomesPage() {
  const location = useLocation();
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    county: 'All Counties',
    gender: 'Any Gender',
    availability: 'Availability'
  });
  const openPropertyDetails = (property: any) => {
    setSelectedProperty(property);
    setIsModalOpen(true);
  };
  // Initialize filters from navigation state if present
  useEffect(() => {
    if (location.state?.filters) {
      setFilters(prev => ({
        ...prev,
        county: location.state.filters.county,
        gender: location.state.filters.gender
      }));
    }
  }, [location.state]);
  const properties = [{
    id: 1,
    name: 'The Silver Spring Nest',
    address: '123 Main St, Silver Spring, MD 20910',
    county: 'Montgomery',
    beds: 2,
    availability: 'Available',
    img: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&q=80&w=800',
    amenities: ['Wi-Fi', 'Laundry', 'Parking', 'Near Metro']
  }, {
    id: 2,
    name: 'Baltimore Harbor View',
    address: '456 Harbor Dr, Baltimore, MD 21202',
    county: 'Baltimore City',
    beds: 1,
    availability: 'Limited',
    img: 'https://images.unsplash.com/photo-1576941089067-2de3c901e126?auto=format&fit=crop&q=80&w=800',
    amenities: ['Wi-Fi', 'Furnished', 'Utilities Incl.', 'Bus Access']
  }, {
    id: 3,
    name: 'Towson Community Home',
    address: '789 College Ave, Towson, MD 21204',
    county: 'Baltimore County',
    beds: 0,
    availability: 'Waitlist',
    img: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&q=80&w=800',
    amenities: ['Wi-Fi', 'Quiet Area', 'Modern Kitchen', 'Garden']
  }, {
    id: 4,
    name: 'Bethesda Quiet Living',
    address: '321 Elm St, Bethesda, MD 20814',
    county: 'Montgomery',
    beds: 3,
    availability: 'Available',
    img: 'https://images.unsplash.com/photo-1598228723793-52759bba239c?auto=format&fit=crop&q=80&w=800',
    amenities: ['Wi-Fi', 'Private Rooms', 'Security', 'Near Shops']
  }];
  return <div className="w-full" data-id="element-188">
      {/* Header */}
      <section className="py-16 md:py-20 bg-primary text-white px-6" data-id="element-189">
        <div className="max-w-7xl mx-auto" data-id="element-190">
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-4 md:mb-6" data-id="element-191">
            Available Homes
          </h1>
          <p className="text-lg md:text-xl text-white/70 max-w-2xl" data-id="element-192">
            Find your next home in one of our professionally managed residential
            communities across Maryland.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-[72px] md:top-[88px] z-40 bg-white border-b border-gray-100 px-6 py-4 shadow-sm" data-id="element-193">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3 md:gap-4" data-id="element-194">
          <div className="w-full md:flex-1 min-w-[200px] relative" data-id="element-195">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={18} data-id="element-196" />
            <input type="text" value={filters.search} onChange={e => setFilters({
            ...filters,
            search: e.target.value
          })} placeholder="Search by city or zip..." className="w-full pl-10 pr-4 py-2 bg-gray-50 border-none rounded-lg focus:ring-2 focus:ring-primary" data-id="element-197" />
          </div>
          <select value={filters.county} onChange={e => setFilters({
          ...filters,
          county: e.target.value
        })} className="px-4 py-2 bg-gray-50 border-none rounded-lg focus:ring-2 focus:ring-primary appearance-none min-w-[150px]" data-id="element-198">
            <option data-id="element-199">All Counties</option>
            <option data-id="element-200">Baltimore City</option>
            <option data-id="element-201">Baltimore County</option>
            <option data-id="element-202">Montgomery County</option>
          </select>
          <select value={filters.gender} onChange={e => setFilters({
          ...filters,
          gender: e.target.value
        })} className="px-4 py-2 bg-gray-50 border-none rounded-lg focus:ring-2 focus:ring-primary appearance-none min-w-[150px]" data-id="element-203">
            <option data-id="element-204">Any Gender</option>
            <option data-id="element-205">Male Only</option>
            <option data-id="element-206">Female Only</option>
          </select>
          <select value={filters.availability} onChange={e => setFilters({
          ...filters,
          availability: e.target.value
        })} className="px-4 py-2 bg-gray-50 border-none rounded-lg focus:ring-2 focus:ring-primary appearance-none min-w-[150px]" data-id="element-207">
            <option data-id="element-208">Availability</option>
            <option data-id="element-209">Available Now</option>
            <option data-id="element-210">Waitlist</option>
          </select>
          <Button size="sm" className="w-full md:w-auto flex items-center justify-center space-x-2" data-id="element-211">
            <Filter size={16} data-id="element-212" />
            <span data-id="element-213">Apply Filters</span>
          </Button>
        </div>
      </section>

      {/* Property Grid */}
      <section className="py-20 px-6 bg-background" data-id="element-214">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-215">
          {properties.map(property => <motion.div key={property.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white rounded-3xl overflow-hidden shadow-premium border border-gray-100 flex flex-col lg:flex-row" data-id="element-216">
              <div className="lg:w-2/5 relative h-64 lg:h-auto" data-id="element-217">
                <img src={property.img} alt={property.name} className="w-full h-full object-cover" data-id="element-218" />
                <div className={cn('absolute top-4 left-4 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider', property.availability === 'Available' ? 'bg-green-500 text-white' : property.availability === 'Limited' ? 'bg-accent text-charcoal' : 'bg-gray-500 text-white')} data-id="element-219">
                  {property.availability}
                </div>
              </div>
              <div className="lg:w-3/5 p-8 flex flex-col" data-id="element-220">
                <div className="flex justify-between items-start mb-4" data-id="element-221">
                  <div data-id="element-222">
                    <div className="text-xs font-bold text-accent uppercase tracking-widest mb-1" data-id="element-223">
                      {property.county} County
                    </div>
                    <h3 className="text-2xl font-bold text-charcoal" data-id="element-224">
                      {property.name}
                    </h3>
                  </div>
                </div>

                <div className="flex items-center text-muted text-sm mb-6" data-id="element-225">
                  <MapPin size={16} className="mr-2 shrink-0" data-id="element-226" />
                  <span data-id="element-227">{property.address}</span>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-228">
                  <div className="flex items-center space-x-2 text-sm text-charcoal font-medium" data-id="element-229">
                    <Users size={16} className="text-primary" data-id="element-230" />
                    <span data-id="element-231">{property.beds} Beds Available</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-charcoal font-medium" data-id="element-232">
                    <Calendar size={16} className="text-primary" data-id="element-233" />
                    <span data-id="element-234">Move-in Ready</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-8" data-id="element-235">
                  {property.amenities.map(amenity => <span key={amenity} className="px-3 py-1 bg-gray-50 text-muted text-xs font-bold rounded-full border border-gray-100" data-id="element-236">
                      {amenity}
                    </span>)}
                </div>

                <div className="mt-auto grid grid-cols-2 gap-4" data-id="element-237">
                  <Button variant="primary" to="/contact" data-id="element-238">
                    Apply Now
                  </Button>
                  <Button variant="outline" onClick={() => openPropertyDetails(property)} data-id="element-239">
                    View Details
                  </Button>
                </div>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Map Preview */}
      <section className="py-20 px-6" data-id="element-240">
        <div className="max-w-7xl mx-auto" data-id="element-241">
          <div className="bg-gray-100 rounded-[2rem] h-[500px] relative overflow-hidden shadow-premium border border-gray-100" data-id="element-242">
            {/* Simulated Map Background */}
            <div className="absolute inset-0 bg-[#f8f9fa]" data-id="element-243">
              <img src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=2000" alt="Maryland Map" className="w-full h-full object-cover opacity-40 grayscale" data-id="element-244" />
              {/* Map Overlay Grid/Lines simulation */}
              <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
              backgroundImage: 'radial-gradient(#000 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }} data-id="element-245" />
            </div>

            {/* Map Markers */}
            <div className="absolute inset-0 z-10" data-id="element-246">
              {/* Marker 1 - Silver Spring */}
              <motion.div initial={{
              scale: 0,
              opacity: 0
            }} whileInView={{
              scale: 1,
              opacity: 1
            }} transition={{
              delay: 0.2
            }} className="absolute top-[30%] left-[45%] group cursor-pointer" data-id="element-247">
                <div className="relative" data-id="element-248">
                  <div className="absolute -inset-4 bg-primary/20 rounded-full animate-ping" data-id="element-249" />
                  <div className="relative bg-white p-2 rounded-full shadow-lg border-2 border-primary text-primary group-hover:bg-primary group-hover:text-white transition-colors" data-id="element-250">
                    <MapPin size={20} data-id="element-251" />
                  </div>
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-white px-3 py-1 rounded-lg shadow-xl border border-gray-100 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-252">
                    <span className="text-xs font-bold text-charcoal" data-id="element-253">
                      Silver Spring Nest
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Marker 2 - Baltimore */}
              <motion.div initial={{
              scale: 0,
              opacity: 0
            }} whileInView={{
              scale: 1,
              opacity: 1
            }} transition={{
              delay: 0.4
            }} className="absolute top-[45%] left-[60%] group cursor-pointer" data-id="element-254">
                <div className="relative" data-id="element-255">
                  <div className="absolute -inset-4 bg-primary/20 rounded-full animate-ping" style={{
                  animationDelay: '1s'
                }} data-id="element-256" />
                  <div className="relative bg-white p-2 rounded-full shadow-lg border-2 border-primary text-primary group-hover:bg-primary group-hover:text-white transition-colors" data-id="element-257">
                    <MapPin size={20} data-id="element-258" />
                  </div>
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-white px-3 py-1 rounded-lg shadow-xl border border-gray-100 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-259">
                    <span className="text-xs font-bold text-charcoal" data-id="element-260">
                      Baltimore Harbor View
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Marker 3 - Bethesda */}
              <motion.div initial={{
              scale: 0,
              opacity: 0
            }} whileInView={{
              scale: 1,
              opacity: 1
            }} transition={{
              delay: 0.6
            }} className="absolute top-[35%] left-[40%] group cursor-pointer" data-id="element-261">
                <div className="relative" data-id="element-262">
                  <div className="absolute -inset-4 bg-primary/20 rounded-full animate-ping" style={{
                  animationDelay: '0.5s'
                }} data-id="element-263" />
                  <div className="relative bg-white p-2 rounded-full shadow-lg border-2 border-primary text-primary group-hover:bg-primary group-hover:text-white transition-colors" data-id="element-264">
                    <MapPin size={20} data-id="element-265" />
                  </div>
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-white px-3 py-1 rounded-lg shadow-xl border border-gray-100 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-266">
                    <span className="text-xs font-bold text-charcoal" data-id="element-267">
                      Bethesda Quiet Living
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Map Controls / UI Overlay */}
            <div className="absolute top-6 right-6 z-20 flex flex-col space-y-2" data-id="element-268">
              <div className="bg-white p-2 rounded-xl shadow-premium border border-gray-100 flex flex-col space-y-2" data-id="element-269">
                <button className="p-2 hover:bg-gray-50 rounded-lg text-muted transition-colors" data-id="element-270">
                  +
                </button>
                <div className="h-px bg-gray-100 mx-2" data-id="element-271" />
                <button className="p-2 hover:bg-gray-50 rounded-lg text-muted transition-colors" data-id="element-272">
                  −
                </button>
              </div>
            </div>

            {/* Content Overlay */}
            <div className="absolute bottom-8 left-8 right-8 md:right-auto md:w-[350px] z-20" data-id="element-273">
              <div className="bg-white/95 backdrop-blur-md p-8 rounded-[2rem] shadow-premium border border-white/20" data-id="element-274">
                <div className="flex items-center space-x-3 mb-4" data-id="element-275">
                  <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary" data-id="element-276">
                    <MapPin size={20} data-id="element-277" />
                  </div>
                  <h3 className="text-xl font-bold text-charcoal" data-id="element-278">
                    Interactive Map
                  </h3>
                </div>
                <p className="text-muted text-sm mb-6 leading-relaxed" data-id="element-279">
                  Visualize our premium independent living communities across
                  the Maryland region. Find the perfect location near your
                  preferred amenities.
                </p>
                <Button variant="primary" className="w-full shadow-lg shadow-primary/20" data-id="element-280">
                  Launch Full Map View
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PropertyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} property={selectedProperty} data-id="element-281" />
    </div>;
}